/**
 * NosAKIntelligence — /nosak/intelligence
 * Consolidated operational intelligence.
 * Merges: Manufacturing, Agriculture, Circularity, Energy, Climate Risk.
 */
import React, { useState, useEffect } from "react";
import { useNosak } from "@/context/NOSAKContext";
import client from "@/api/client";

const fmtN = (v, u = "") => { if (!v && v !== 0) return "—"; const n = Number(v); if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M ${u}`.trim(); if (n >= 1e3) return `${(n / 1e3).toFixed(0)}K ${u}`.trim(); return `${n.toLocaleString()} ${u}`.trim(); };
const fmtPct = v => `${Number(v || 0).toFixed(1)}%`;

const BarRow = ({ label, value, max, color, sub, count }) => {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="space-y-1 py-2 border-b border-slate-50 last:border-0">
      <div className="flex justify-between text-xs">
        <span className="font-medium text-slate-700 capitalize max-w-[180px] truncate">{label.replace(/_/g, " ")}</span>
        <div className="flex items-center gap-2 flex-shrink-0">
          {count !== undefined && <span className="text-[10px] text-slate-400">{count}</span>}
          <span className="font-black text-xs" style={{ color }}>{fmtN(value)}</span>
        </div>
      </div>
      <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: pct + "%", background: color }} />
      </div>
      {sub && <p className="text-[9px] text-slate-400">{sub}</p>}
    </div>
  );
};

const TABS = [
  { id: "emissions",   label: "Emissions" },
  { id: "agriculture", label: "Agriculture" },
  { id: "industrial",  label: "Industrial" },
  { id: "circularity", label: "Circularity" },
  { id: "reduction",   label: "Reduction" },
];

export default function NosAKIntelligence() {
  const { group, subsidiaries, loading, selectedPeriod } = useNosak();
  const [activeTab, setActiveTab] = useState("emissions");
  const [agData, setAgData] = useState(null);
  const [projData, setProjData] = useState([]);

  useEffect(() => {
    if (!selectedPeriod) return;
    client.get(`/nosak/agriculture-intelligence/${selectedPeriod}`).then(r => setAgData(r.data)).catch(() => {});
    client.get(`/nosak/project-analytics/${selectedPeriod}`).then(r => setProjData(r.data?.projects || [])).catch(() => {});
  }, [selectedPeriod]);

  if (loading) return <div className="p-8 text-slate-400 text-sm">Loading…</div>;

  const byDiv = group.by_division || {};
  const scope = group.scope_split || {};
  const redB = group.reduction_breakdown || {};
  const divEntries = Object.entries(byDiv).sort((a, b) => b[1].tco2e - a[1].tco2e);
  const maxTco2e = Math.max(...divEntries.map(([, d]) => d.tco2e), 1);
  const maxRed = Math.max(...divEntries.map(([, d]) => d.reduction), 1);
  const maxCirc = Math.max(...divEntries.map(([, d]) => d.avg_circ || 0), 1);

  const brc = agData?.biomass_residue_chain || {};
  const rr = agData?.regenerative_readiness || {};
  const hs = agData?.methane_hotspots || [];
  const n2o = agData?.n2o_from_fertilizer || {};
  const sc = agData?.soil_carbon_opportunity || {};

  // HFO dependency
  const distSub = subsidiaries.find(s => s.division === "distilleries");
  const hfoCO2 = distSub?.calc?.scope1?.hfo || 0;
  const totalS1 = group.scope1_tco2e || 1;
  const hfoPct = Math.round(hfoCO2 / totalS1 * 100);

  return (
    <div className="p-6 lg:p-8 bg-[#F8FAFC] min-h-screen space-y-5">
      <div>
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded">Nosak Group</span>
        <h1 className="text-2xl font-black text-slate-900 mt-1.5">Climate & Circularity Intelligence</h1>
        <p className="text-xs text-slate-400 mt-0.5">
          Emissions · Agriculture · Industrial · Circularity · Reduction — consolidated analytical workspace
        </p>
      </div>

      {/* Strip KPIs */}
      <div className="grid grid-cols-4 gap-3">
        {[
          ["Total Emissions", fmtN(group.total_tco2e, "tCO₂e/yr"), "#64748b"],
          ["HFO Dependency", `${hfoPct}% of Scope 1`, "#dc2626"],
          ["Avg Circularity", (group.circularity_score_avg || 0).toFixed(1) + "/100", "#0891b2"],
          ["Reduction Potential", fmtN(group.reduction_potential_tco2e, "tCO₂e/yr"), "#059669"],
        ].map(([k, v, c]) => (
          <div key={k} className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
            <p className="text-[10px] text-slate-400">{k}</p>
            <p className="text-xl font-black mt-1" style={{ color: c }}>{v}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-white border border-slate-200 rounded-xl p-1.5">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`flex-1 py-2 px-2 rounded-lg text-xs font-medium transition-all ${activeTab === t.id ? "bg-[#0f172a] text-white" : "text-slate-500 hover:bg-slate-50"}`}
            data-testid={`tab-${t.id}`}>{t.label}
          </button>
        ))}
      </div>

      {/* EMISSIONS */}
      {activeTab === "emissions" && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
            <p className="text-xs font-bold text-slate-700 mb-3">Emissions by Division (tCO₂e/yr)</p>
            {divEntries.map(([d, data]) => (
              <BarRow key={d} label={d} value={data.tco2e} max={maxTco2e} color="#64748b"
                count={`${data.count} subs`} sub={fmtPct(data.tco2e / (group.total_tco2e || 1) * 100) + " of total"} />
            ))}
          </div>
          <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
            <p className="text-xs font-bold text-slate-700 mb-3">Scope Split</p>
            {[["Scope 1 — Direct Combustion", scope.s1_pct || 0, "#64748b", "HFO, diesel, process, N₂O"],
              ["Scope 2 — Grid Electricity", scope.s2_pct || 0, "#94a3b8", "Nigeria NERC 2022: 0.43 kgCO₂e/kWh"],
              ["Scope 3 — Value Chain", scope.s3_pct || 0, "#cbd5e1", "Logistics, upstream agri, packaging"]
            ].map(([label, pct, color, sub]) => (
              <div key={label} className="py-2 border-b border-slate-50 last:border-0">
                <div className="flex justify-between text-xs mb-1">
                  <span className="font-medium text-slate-700">{label}</span>
                  <span className="font-black" style={{ color }}>{fmtPct(pct)}</span>
                </div>
                <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: pct + "%", background: color }} />
                </div>
                <p className="text-[9px] text-slate-400 mt-0.5">{sub}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AGRICULTURE */}
      {activeTab === "agriculture" && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
            <p className="text-xs font-bold text-slate-700 mb-3">Biomass Residue Chain</p>
            {[["EFB (Empty Fruit Bunch)", brc.efb_t_yr, "t/yr", "#059669"],
              ["Palm Shell", brc.palm_shell_t_yr, "t/yr", "#0891b2"],
              ["POME (Effluent)", brc.pome_m3_yr, "m³/yr", "#d97706"],
              ["Biomass Recovered", brc.biomass_recovered_t, "t/yr", "#059669"],
              ["Recovery Gap", brc.gap_t, "t/yr", "#dc2626"],
            ].map(([k, v, u, c]) => (
              <div key={k} className="flex justify-between py-1.5 border-b border-slate-50 last:border-0 text-xs">
                <span className="text-slate-600">{k}</span>
                <span className="font-bold" style={{ color: c }}>{fmtN(v, u)}</span>
              </div>
            ))}
          </div>
          <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm space-y-4">
            <div>
              <p className="text-xs font-bold text-slate-700 mb-2">Methane Hotspots</p>
              {hs.length === 0 ? <p className="text-xs text-slate-400">No priority hotspots at current scale</p>
                : hs.map(h => (
                  <div key={h.source} className="bg-amber-50 border border-amber-100 rounded-lg p-3 mb-2">
                    <p className="text-[10px] font-bold text-amber-800">{h.source}</p>
                    <p className="text-[9px] text-amber-700">{h.scale} · {h.pathway}</p>
                  </div>
                ))}
            </div>
            <div>
              <p className="text-xs font-bold text-slate-700 mb-2">Regenerative Agriculture</p>
              <div className="flex items-center gap-3">
                <p className="text-3xl font-black" style={{ color: rr.score >= 70 ? "#059669" : rr.score >= 40 ? "#d97706" : "#dc2626" }}>{rr.score}/100</p>
                <div>
                  <p className="text-xs font-bold text-slate-700">{rr.maturity}</p>
                  <p className="text-[9px] text-slate-400">Soil carbon: {sc.screening_result}</p>
                </div>
              </div>
              <p className="text-[9px] text-amber-700 mt-2 bg-amber-50 rounded p-2">{sc.caveat?.slice(0, 100)}…</p>
            </div>
          </div>
        </div>
      )}

      {/* INDUSTRIAL */}
      {activeTab === "industrial" && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
            <p className="text-xs font-bold text-slate-700 mb-3">Industrial Intensity — HFO Dependency</p>
            <div className="bg-red-50 border border-red-100 rounded-xl p-4 mb-3">
              <p className="text-xs font-bold text-red-800">HFO = {hfoPct}% of Group Scope 1</p>
              <p className="text-[10px] text-red-700 mt-1">Primary decarbonisation lever. Biomass conversion = highest reduction leverage per $ CAPEX invested.</p>
            </div>
            {subsidiaries.map(s => {
              const hfo = s.calc?.scope1?.hfo || 0;
              const total = s.calc?.total_tco2e || 1;
              return (
                <div key={s.sub_id} className="py-1.5 border-b border-slate-50 last:border-0">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-medium text-slate-700 truncate max-w-[140px]">{s.name}</span>
                    <span className="text-slate-500">{fmtN(s.calc?.emissions_intensity || 0, "tCO₂e/unit")}</span>
                  </div>
                  <div className="w-full h-1 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-slate-400" style={{ width: Math.min(100, (s.calc?.total_tco2e || 0) / (group.total_tco2e || 1) * 100) + "%" }} />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
            <p className="text-xs font-bold text-slate-700 mb-3">Scope 1 Breakdown by Source</p>
            {[["HFO Combustion", redB.hfo_fuel_switch || 0, "#dc2626"],
              ["Process Emissions", redB.methane_capture || 0, "#d97706"],
              ["Fleet Diesel", redB.fleet_transition || 0, "#6366f1"],
              ["Agriculture N₂O", redB.precision_agriculture || 0, "#059669"],
            ].map(([k, v, c]) => (
              <div key={k} className="py-1.5 border-b border-slate-50 last:border-0 flex justify-between text-xs">
                <span className="text-slate-600">{k}</span>
                <span className="font-bold" style={{ color: c }}>{fmtN(v, "tCO₂e/yr reduction")}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CIRCULARITY */}
      {activeTab === "circularity" && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
            <p className="text-xs font-bold text-slate-700 mb-3">Circularity Score by Division</p>
            {divEntries.map(([d, data]) => (
              <BarRow key={d} label={d} value={data.avg_circ || 0} max={100}
                color={data.avg_circ >= 60 ? "#059669" : data.avg_circ >= 40 ? "#d97706" : "#dc2626"}
                sub="Composite: diversion×40% + biomass×30% + energy×20% + packaging×10%" />
            ))}
          </div>
          <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
            <p className="text-xs font-bold text-slate-700 mb-3">Subsidiary Circularity Detail</p>
            {subsidiaries.map(s => (
              <div key={s.sub_id} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                <div className="min-w-0">
                  <p className="text-xs font-medium text-slate-700 truncate">{s.name}</p>
                  <p className="text-[9px] text-slate-400">{fmtPct(s.calc?.waste_diversion_pct || 0)} diversion</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: (s.circularity_score || 0) + "%", background: (s.circularity_score || 0) >= 60 ? "#059669" : "#d97706" }} />
                  </div>
                  <span className="text-xs font-black text-slate-700">{(s.circularity_score || 0).toFixed(0)}/100</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* REDUCTION */}
      {activeTab === "reduction" && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
            <p className="text-xs font-bold text-slate-700 mb-3">Reduction by Division</p>
            {divEntries.map(([d, data]) => (
              <BarRow key={d} label={d} value={data.reduction} max={maxRed}
                color="#059669" count={`${data.count} subs`}
                sub={fmtPct(data.tco2e > 0 ? (data.reduction / data.tco2e) * 100 : 0) + " reduction rate"} />
            ))}
          </div>
          <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
            <p className="text-xs font-bold text-slate-700 mb-3">Top Projects — Ranked by Readiness × Reduction</p>
            {projData.slice(0, 6).map(p => (
              <div key={p.project_id} className="py-2 border-b border-slate-50 last:border-0">
                <div className="flex justify-between text-xs mb-0.5">
                  <span className="font-medium text-slate-700 truncate max-w-[180px]">{p.name}</span>
                  <span className="text-emerald-700 font-bold flex-shrink-0">{fmtN(p.reduction_tco2e_yr)} tCO₂e</span>
                </div>
                <div className="flex items-center gap-2 text-[9px]">
                  <span className="text-slate-400">{p.complexity} complexity</span>
                  <span className="text-blue-600 font-medium">Readiness {p.readiness_score}/100</span>
                  <span className="text-amber-600">{p.simple_payback_yrs}yr payback</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
